// Template metadata for the frontend UI
// Template rendering is handled by functions/src/sites/templates/ via @shared/siteRenderer

import { getAllTemplateMetadata, getTemplateTheme } from '@shared/siteRenderer';

// Keep LayoutType for backward compat with components that reference it
export type LayoutType = string;

export interface Template {
  id: string;
  name: string;
  category: string;
  layout: LayoutType;
  preview: string;
  previewImage: string;
  description: string;
  themeEditable: boolean;
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
    bodyText: string;
  };
  typography: {
    fontHeading: string;
    fontBody: string;
  };
  style: {
    borderRadius: string;
    gradient: string;
    headerStyle: 'transparent' | 'solid' | 'minimal';
    cardStyle: 'flat' | 'elevated' | 'bordered' | 'glass';
  };
}

// Build TEMPLATES array from the new registry metadata
const registryMetadata = getAllTemplateMetadata();

export const TEMPLATES: Template[] = registryMetadata.map((meta) => {
  const theme = getTemplateTheme(meta.id);
  return {
    id: meta.id,
    name: meta.name,
    category: meta.category,
    layout: '' as LayoutType,
    preview: '',
    previewImage: meta.previewImage,
    description: meta.description,
    themeEditable: meta.themeEditable !== false,
    theme: {
      primary: theme.primaryColor || '',
      secondary: '',
      accent: theme.accentColor || '',
      background: theme.bgColor || '',
      text: theme.textColor || '',
      bodyText: theme.bodyTextColor || '',
    },
    typography: {
      fontHeading: theme.headingFont || '',
      fontBody: theme.bodyFont || '',
    },
    style: { borderRadius: '8px', gradient: '', headerStyle: 'solid' as const, cardStyle: 'flat' as const },
  };
});

// Derive categories from actual templates, with "All" first
const categorySet = new Set(TEMPLATES.map(t => t.category));
export const TEMPLATE_CATEGORIES = ['All', ...Array.from(categorySet)];

export function getTemplatesByCategory(category: string): Template[] {
  if (category === 'All') return TEMPLATES;
  return TEMPLATES.filter((t) => t.category === category);
}

export function getTemplateById(id: string): Template | undefined {
  return TEMPLATES.find((t) => t.id === id);
}
